import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signInWithGoogle } from '../api/auth';
import { persistPendingSearch, goToLogin, goToSignup } from '../utils/guestSearch';
import { persistPendingConsent } from '../utils/legal';
import '../styles/searchStates.css';
import '../styles/legal.css';

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <path d="M19.6 10.23c0-.68-.06-1.36-.18-2.02H10v3.82h5.44a4.64 4.64 0 01-2.01 3.05v2.54h3.24c1.9-1.75 2.99-4.33 2.99-7.39z" fill="#4285F4" />
    <path d="M10 20c2.7 0 4.97-.9 6.63-2.44l-3.24-2.54c-.9.6-2.05.96-3.39.96-2.6 0-4.8-1.76-5.59-4.13H1.07v2.59A10 10 0 0010 20z" fill="#34A853" />
    <path d="M4.41 11.85A5.99 5.99 0 014.09 10c0-.64.11-1.26.32-1.85V5.56H1.07A10 10 0 000 10c0 1.64.39 3.19 1.07 4.44l3.34-2.59z" fill="#FBBC05" />
    <path d="M10 4.04c1.47 0 2.78.51 3.81 1.5l2.86-2.86C14.97 1.13 12.7.04 10 .04A10 10 0 001.07 5.56l3.34 2.59C5.2 5.8 7.4 4.04 10 4.04z" fill="#EA4335" />
  </svg>
);

/**
 * Guest daily-quota sheet. Reuses search-failed layout, not a modal.
 */
const GuestQuotaSheet = ({
  query,
  onBack,
}) => {
  const navigate = useNavigate();
  const [googleLoading, setGoogleLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState('');

  const title = 'Sign in to keep searching';
  const body = "You've used today's guest searches. Create a free account to keep going, get notified when something goes live, and save what you follow.";

  const handleGoogle = async () => {
    if (!acceptedTerms) {
      setError('Accept Privacy and Terms to create an account.');
      return;
    }
    setError('');
    setGoogleLoading(true);
    persistPendingSearch(query);
    persistPendingConsent();
    try {
      await signInWithGoogle();
    } catch (err) {
      setGoogleLoading(false);
      setError(err.message || 'Google sign-in failed');
    }
  };

  return (
    <div className="search-failed-state guest-quota-sheet">
      <div className="search-failed-content">
        <div className="search-failed-message">
          <h2 className="search-failed-title">{title}</h2>
          <p className="search-failed-subtitle">{body}</p>
        </div>

        {error ? (
          <p className="guest-quota-error" role="alert">{error}</p>
        ) : null}

        <label className="legal-consent">
          <input
            type="checkbox"
            checked={acceptedTerms}
            onChange={(e) => setAcceptedTerms(e.target.checked)}
          />
          <span>
            I agree to the <Link to="/privacy">Privacy Policy</Link> and <Link to="/terms">Terms of Service</Link>.
          </span>
        </label>

        <div className="search-failed-actions guest-quota-actions">
          <button
            type="button"
            className="guest-quota-google"
            onClick={handleGoogle}
            disabled={googleLoading || !acceptedTerms}
          >
            <GoogleIcon />
            {googleLoading ? 'Connecting...' : 'Continue with Google'}
          </button>
          <button
            type="button"
            className="search-retry-button"
            onClick={() => goToSignup(navigate, query)}
          >
            Sign up with email
          </button>
          <button
            type="button"
            className="search-retry-button"
            onClick={() => goToLogin(navigate, query)}
          >
            Log in
          </button>
          {onBack && (
            <button
              type="button"
              className="search-back-button"
              onClick={onBack}
              aria-label="Go back to search"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M15 18l-6-6 6-6" />
              </svg>
              Back to Search
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default GuestQuotaSheet;
